import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  ArrowRight,
  QrCode,
  BookOpen,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  Clock,
  Sparkles,
  FileText,
  Sliders,
} from "lucide-react";

export const NixlevConcept2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgWarm = "#F5F3ED";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#10B981";
  const goldAccent = "#D4A346";
  const textDark = "#121814";

  const SUBTITLES = [
    { from: 0, to: 105, text: "Berapa lama waktu yang kamu butuhkan untuk mulai jualan online?", highlight: "Berapa lama waktu" },
    { from: 105, to: 240, text: "Di Nixlev, jawabannya adalah: 3 langkah, 5 menit.", highlight: "3 langkah, 5 menit." },
    { from: 240, to: 420, text: "Langkah 1: Upload karyamu. E-book, preset, template, atau jasa.", highlight: "Langkah 1: Upload karyamu" },
    { from: 420, to: 640, text: "Langkah 2: Bagikan satu link Nixlev yang bersih & terpercaya.", highlight: "Langkah 2: Bagikan satu link" },
    { from: 640, to: 800, text: "Langkah 3: Biarkan mesin otomatisasi bekerja.", highlight: "Langkah 3: Mesin otomatisasi" },
    { from: 800, to: 1050, text: "Selesai transaksi QRIS, file & invoice langsung meluncur ke WA & Email.", highlight: "meluncur ke WA & Email" },
    { from: 1050, to: 1280, text: "Kamu tetap santai, tokomu melayani pembeli 24 jam nonstop.", highlight: "24 jam nonstop" },
    { from: 1280, to: 1390, text: "Nol biaya hosting. Nol biaya tersembunyi.", highlight: "Nol biaya hosting" },
    { from: 1390, to: 1560, text: "Buka tokomu gratis hari ini di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: frame >= 1390 ? greenForest : bgWarm,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: frame >= 1390 ? "#FFFFFF" : textDark,
        overflow: "hidden",
        transition: "background-color 0.4s ease",
      }}
    >
      <Audio src={staticFile("nixlev_concept2_orus.mp3")} />

      {/* SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 105 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 240 && <Audio src={staticFile("whoosh.mp3")} volume={0.45} />}
      {frame === 420 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 640 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 800 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 1390 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {/* INTRO: VELOCITY QUESTION (0 - 240) */}
      {frame < 240 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 40px",
          }}
        >
          {/* Speedometer Badge */}
          <div
            style={{
              padding: "12px 28px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 35,
            }}
          >
            <Clock size={28} />
            Velocity Machine
          </div>

          <h1
            style={{
              fontSize: 64,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 40,
              letterSpacing: "-0.03em",
            }}
          >
            Mulai Jualan Online <br />
            Hanya Dalam <span style={{ color: "#16A34A" }}>5 Menit.</span>
          </h1>

          {/* 3 Steps Big Badges */}
          <div style={{ display: "flex", gap: 16 }}>
            {["01. Upload Karya", "02. Sebar Link", "03. Auto Cuan"].map((st, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#FFFFFF",
                  padding: "16px 24px",
                  borderRadius: 20,
                  fontSize: 22,
                  fontWeight: 800,
                  border: "2px solid #E5E7EB",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                }}
              >
                {st}
              </div>
            ))}
          </div>
        </AbsoluteFill>
      )}

      {/* STEP 1: UPLOAD KARYA (240 - 420) */}
      {frame >= 240 && frame < 420 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#EFF6FF",
              color: "#2563EB",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            LANGKAH 01
          </div>

          <h2 style={{ fontSize: 52, fontWeight: 900, marginBottom: 35, textAlign: "center" }}>
            Upload Apa Saja Karyamu
          </h2>

          <div style={{ width: "100%", maxWidth: 840, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {[
              { title: "E-Book PDF", icon: BookOpen, color: "#2563EB", bg: "#EFF6FF" },
              { title: "Preset Lightroom", icon: Sliders, color: "#7C3AED", bg: "#F5F3FF" },
              { title: "Template Notion", icon: FileText, color: "#D97706", bg: "#FFFBEB" },
              { title: "Jasa Konsultasi", icon: Briefcase, color: "#059669", bg: "#ECFDF5" },
            ].map((p, idx) => {
              const IconComp = p.icon;
              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 22,
                    padding: "24px 28px",
                    border: "2px solid #E5E7EB",
                    boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                  }}
                >
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 16,
                      backgroundColor: p.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComp size={32} color={p.color} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{p.title}</div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* STEP 2: SHARE ONE CLEAN LINK + REAL DESKTOP (420 - 640) */}
      {frame >= 420 && frame < 640 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "70px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#FDF4FF",
              color: "#A855F7",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            LANGKAH 02
          </div>

          <h2 style={{ fontSize: 50, fontWeight: 900, marginBottom: 30, textAlign: "center" }}>
            Bagikan 1 Link Toko Elegan
          </h2>

          <div
            style={{
              width: 920,
              height: 520,
              backgroundColor: "#FFFFFF",
              borderRadius: 24,
              border: "1px solid #E5E7EB",
              boxShadow: "0 25px 60px rgba(0,0,0,0.12)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                height: 48,
                backgroundColor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <div
                style={{
                  flex: 1,
                  height: 32,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#374151",
                  gap: 8,
                }}
              >
                <Globe size={16} color="#10B981" />
                nixlev.com/@kreator
              </div>
            </div>
            <Img
              src={staticFile("nixlev_desktop.png")}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }}
            />
          </div>
        </AbsoluteFill>
      )}

      {/* STEP 3: AUTOMATED DISPATCH VIA QRIS (640 - 1390) */}
      {frame >= 640 && frame < 1390 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "70px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            LANGKAH 03
          </div>

          <h2 style={{ fontSize: 50, fontWeight: 900, marginBottom: 35, textAlign: "center" }}>
            Mesin Otomatis Melayani 24 Jam
          </h2>

          <div
            style={{
              width: 360,
              backgroundColor: "#111827",
              borderRadius: 28,
              padding: "24px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
              marginBottom: 30,
            }}
          >
            <div style={{ width: 180, height: 180, backgroundColor: "#FFF", borderRadius: 16, padding: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <QrCode size={160} color="#111827" />
            </div>
            <div style={{ marginTop: 14, fontSize: 18, fontWeight: 800, color: "#10B981" }}>
              QRIS Instan Terverifikasi
            </div>
          </div>

          {/* Dual Delivery Cards */}
          <div style={{ width: "100%", maxWidth: 840, display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 20,
                padding: "20px 26px",
                border: "2px solid #22C55E",
                boxShadow: "0 10px 30px rgba(34, 197, 94, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 18,
              }}
            >
              <MessageSquare size={34} color="#15803D" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#14532D" }}>Kirim Link Download ke WhatsApp</div>
                <div style={{ fontSize: 16, color: "#4B5563" }}>Bot mengirimkan file secara otomatis detik itu juga.</div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 20,
                padding: "20px 26px",
                border: "2px solid #3B82F6",
                boxShadow: "0 10px 30px rgba(59, 130, 246, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 18,
              }}
            >
              <Mail size={34} color="#2563EB" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#1E3A8A" }}>Kirim Invoice & File ke Email</div>
                <div style={{ fontSize: 16, color: "#4B5563" }}>Bukti transaksi sah dan lisensi produk aman.</div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* OUTRO: GREEN THEME NIXLEV (1390 - 1560) */}
      {frame >= 1390 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "80px 40px",
          }}
        >
          {/* Logo with Emerald Glow */}
          <div
            style={{
              padding: 24,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: `3px solid ${goldAccent}`,
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.45)",
              transform: `scale(${spring({
                frame: frame - 1390,
                fps,
                config: { damping: 14, stiffness: 120 },
              })})`,
              marginBottom: 25,
            }}
          >
            <Img src={staticFile("nixlev_logo.png")} style={{ width: 170, height: 170, objectFit: "contain" }} />
          </div>

          <h1 style={{ fontSize: 62, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 25 }}>
            3 Langkah. 5 Menit. <br />
            <span style={{ color: "#34D399" }}>Langsung Buka Tokomu.</span>
          </h1>

          <div
            style={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              padding: "24px 54px",
              borderRadius: 32,
              fontSize: 32,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              gap: 18,
              boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span>Buka Toko Gratis di nixlev.com</span>
            <ArrowRight size={36} color="#FFFFFF" />
          </div>
        </AbsoluteFill>
      )}

      {/* SUBTITLES SAFE ZONE */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            padding: "0 40px",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(18, 24, 20, 0.90)",
              backdropFilter: "blur(12px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.14)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 920,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 30, fontWeight: 800, color: "#FFFFFF" }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: frame >= 1390 ? "#34D399" : goldAccent }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
